import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import { Btn } from '../../components/Btn';
import { KpiCard } from '../../components/KpiCard';
import { Pill, type PillTone } from '../../components/Pill';
import { DataTable } from '../../components/Table';
import { listPromotions } from '../../data/fetchers';
import { fmtAmount } from '../../lib/format';
import { canEditPromotions } from '../../lib/permissions';
import { useSession } from '../../stores/session';
import type { PromoStatus, Promotion, PromoType } from '../../types/domain';
import { PromotionFormView } from './PromotionFormView';
import { SettingsHeader } from './SettingsHeader';

const TYPE_LABEL: Record<PromoType, string> = {
  percent: 'ลดเปอร์เซ็นต์',
  baht: 'ลดบาท',
  bogo: 'ซื้อ 1 แถม 1',
  qty_tier: 'ลดตามจำนวนแบบทบชุด',
};
const STATUS_LABEL: Record<PromoStatus, string> = {
  active: 'กำลังใช้งาน',
  scheduled: 'ตั้งเวลา',
  ended: 'สิ้นสุดแล้ว',
};
const STATUS_TONE: Record<PromoStatus, PillTone> = {
  active: 'ok',
  scheduled: 'primary',
  ended: 'neutral',
};

const formatValue = (p: Promotion) =>
  p.type === 'percent'
    ? `${p.value}%`
    : p.type === 'baht'
      ? `฿${fmtAmount(p.value)}`
      : p.type === 'qty_tier'
        ? `ทุก ${p.minQty ?? 0} ชิ้น ลด ฿${fmtAmount(p.value)}`
        : '1+1';

export function PromotionsView() {
  const user = useSession((s) => s.user);
  const canEdit = user ? canEditPromotions(user) : false;
  const {
    data: promotions = [],
    isError,
    error,
  } = useQuery({
    queryKey: ['promotions'],
    queryFn: listPromotions,
  });
  const [editing, setEditing] = useState<Promotion | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const activeCount = promotions.filter(
    (item) => item.status === 'active',
  ).length;
  const scheduledCount = promotions.filter(
    (item) => item.status === 'scheduled',
  ).length;
  const endedCount = promotions.filter(
    (item) => item.status === 'ended',
  ).length;

  const openAdd = () => {
    setEditing(null);
    setIsFormOpen(true);
  };
  const openEdit = (p: Promotion) => {
    setEditing(p);
    setIsFormOpen(true);
  };
  const closeForm = () => {
    setEditing(null);
    setIsFormOpen(false);
  };

  const columns = useMemo<ColumnDef<Promotion, unknown>[]>(
    () => [
      { accessorKey: 'name', header: 'ชื่อโปรโมชั่น' },
      {
        id: 'type',
        header: 'ประเภท',
        cell: (c) => TYPE_LABEL[c.row.original.type],
      },
      {
        id: 'value',
        header: 'มูลค่า',
        cell: (c) => <span className="num">{formatValue(c.row.original)}</span>,
      },
      {
        id: 'status',
        header: 'สถานะ',
        cell: (c) => (
          <Pill tone={STATUS_TONE[c.row.original.status]}>
            {STATUS_LABEL[c.row.original.status]}
          </Pill>
        ),
      },
      {
        id: 'scope',
        header: 'ขอบเขต',
        cell: (c) =>
          c.row.original.scope === 'all'
            ? 'ทุกสาขา'
            : `${c.row.original.branchIds.length} สาขา`,
      },
      ...(canEdit
        ? [
            {
              id: 'go',
              header: '',
              cell: (c) => (
                <button
                  className="text-primary text-sm-label font-bold hover:underline"
                  onClick={() => openEdit(c.row.original)}
                >
                  แก้ไข
                </button>
              ),
            } as ColumnDef<Promotion, unknown>,
          ]
        : []),
    ],
    [canEdit],
  );

  if (isFormOpen && canEdit) {
    return <PromotionFormView promo={editing} onClose={closeForm} />;
  }

  if (isError) {
    const isUnsupportedType =
      error instanceof Error &&
      (error.message.includes('unsupported type') ||
        error.message.includes('bill_percent'));
    return (
      <div className="bo-fade">
        <SettingsHeader
          title="โปรโมชั่น"
          subtitle="สร้างเงื่อนไขส่วนลด กำหนดสินค้า และเลือกสาขาที่ร่วมรายการ"
          status={{ label: 'โหลดข้อมูลไม่สำเร็จ', tone: 'danger' }}
        />
        <div className="rounded-card border border-status-error/40 bg-status-error/5 p-6 mx-4">
          <h3 className="text-h3 font-bold text-status-error mb-2">
            ไม่สามารถโหลดโปรโมชั่นได้
          </h3>
          {isUnsupportedType ? (
            <p className="text-body text-ink-600">
              ระบบพบโปรโมชั่นประเภทที่ dashboard ยังไม่รองรับ (bill_percent)
              กรุณาแก้ไขโปรโมชั่นนี้จากเครื่องมือจัดการ master data
              ที่รองรับประเภทดังกล่าว
            </p>
          ) : (
            <p className="text-body text-ink-600">
              {error instanceof Error
                ? error.message
                : 'เกิดข้อผิดพลาดในการเชื่อมต่อ'}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bo-fade">
      <SettingsHeader
        title="โปรโมชั่น"
        subtitle="สร้างเงื่อนไขส่วนลด กำหนดสินค้า และเลือกสาขาที่ร่วมรายการ"
        status={{
          label:
            activeCount > 0
              ? `${activeCount} โปรโมชั่นกำลังใช้งาน`
              : 'ไม่มีโปรโมชั่นที่กำลังใช้งาน',
          tone: activeCount > 0 ? 'ok' : 'neutral',
        }}
        actions={
          canEdit ? (
            <Btn kind="primary" onClick={openAdd}>
              + เพิ่มโปรโมชั่น
            </Btn>
          ) : undefined
        }
      />
      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <KpiCard
          label="กำลังใช้งาน"
          value={activeCount}
          sub="โปรโมชั่นที่มีผลตอนนี้"
          tone="ok"
        />
        <KpiCard
          label="ตั้งเวลา"
          value={scheduledCount}
          sub="รอวันเริ่มโปรโมชั่น"
          tone="accent"
        />
        <KpiCard
          label="สิ้นสุดแล้ว"
          value={endedCount}
          sub="เก็บไว้เป็นประวัติ"
        />
      </div>
      <DataTable columns={columns} data={promotions} />
    </div>
  );
}
